const EVERYWHERE_PATH = '/everywhere/';
const TESTS_INDEX_PATH = '/everywhere/tests/';

/**
 * EVWR-3 Tier A From slugs. Soft-404 must not claim these as this ticket's
 * work, and static recovery pages must not be generated for them so a later
 * 301 can own the URL.
 */
export const TIER_A_DEAD_PDP_SLUGS: readonly string[] = [
  'quantiferon-tb-gold-plus',
  'total-testosterone',
  'testosterone-total',
  'testosterone-lc-ms',
  'total-testosterone-high-sensitivity-lc-ms',
  'testosterone-free-total-lc-ms',
];

/**
 * EVWR-4 Tier B B1–B42 must-cover dead PDP slugs (lipid-panel → cortisol-free-total-lc-ms)
 * plus the ticket's priority LLM/search leaders (including D-EW-051
 * neurofilament-light-chain-nfl). Catch-all routing covers unmatched
 * `/everywhere/tests/*` URLs only after a static inventory page exists for
 * that slug; unlisted slugs still 404 at S3/CloudFront.
 */
export const TIER_B_SOFT_404_SLUGS: readonly string[] = [
  'lipid-panel',
  'rabies-antibody-titer-rffit',
  'ptau-217-alzheimer-s-blood-marker',
  'lyme-disease-test-2-tier-with-reflex',
  'celiac-genetic-test-hla-dq2-dq8',
  'neurofilament-light-chain-nfl',
  'chlamydia-plus-gonorrhea-throat',
  'coq10',
  'hemoglobin-a1c-with-eag',
  'women-s-health-panel',
  'chlamydia-throat',
  'gonorrhea-throat',
  'cholesterol-panel',
  'cholesterol-test',
  'hba1c',
  'hba1c-eag',
  'a1c-test',
  'womens-health-panel',
  'women-health-panel',
  'food-sensitivity',
  'food-sensitivity-test',
  'lyme-disease',
  'lyme-test',
  'celiac-test',
  'celiac-hla',
  'rabies-titer',
  'alzheimers-blood-test',
  'ptau-217',
  'coenzyme-q10',
  'vitamin-coq10',
  'std-panel',
  'full-std-panel',
  'thyroid-panel',
  'hormone-panel',
  'metabolic-panel',
  'covid-antibody',
  'covid-test',
  'strep-throat',
  'influenza-a-b',
  'mono-test',
  'pregnancy-blood-test',
  'psa-test',
  'cortisol-free-total-lc-ms',
];

/** EVWR-4 Tier C free-T cluster: same recovery until Clinical names a 301. */
export const TIER_C_SOFT_404_SLUGS: readonly string[] = [
  'testosterone-free',
  'free-testosterone',
  'free-t',
];

export const EVERYWHERE_SOFT_404_SLUGS: readonly string[] = [
  ...TIER_B_SOFT_404_SLUGS,
  ...TIER_C_SOFT_404_SLUGS,
];

const TIER_A_SET = new Set(TIER_A_DEAD_PDP_SLUGS);
const SOFT_404_SET = new Set(EVERYWHERE_SOFT_404_SLUGS);

export const EVERYWHERE_TEST_NOT_FOUND_PATH = `${TESTS_INDEX_PATH}not-found/`;

const TESTS_PREFIX = TESTS_INDEX_PATH;

export const shouldCreateEverywhereSoft404Page = (
  urlSlug: string,
  liveUrlSlugs: ReadonlySet<string>,
): boolean => {
  if (!urlSlug || liveUrlSlugs.has(urlSlug)) return false;
  if (TIER_A_SET.has(urlSlug)) return false;
  return SOFT_404_SET.has(urlSlug);
};

export const everywhereTestSlugFromPath = (
  pathname: string | undefined,
): string | null => {
  if (!pathname || !pathname.startsWith(TESTS_PREFIX)) return null;
  const rest = pathname.slice(TESTS_PREFIX.length).replace(/\/+$/, '');
  if (!rest || rest.includes('/')) return null;
  return rest;
};

/** True for `/everywhere/tests/<slug>/` including the shared not-found utility. */
export const isEverywhereTestRecoveryPath = (
  pathname: string | undefined,
): boolean => {
  const slug = everywhereTestSlugFromPath(pathname);
  return Boolean(slug);
};

/**
 * Soft-404 URLs that should stay out of ecommerce-sitemap.xml.
 * Live PDPs (including inventory slugs later revived in the catalog) must
 * remain in the sitemap; only actually generated recovery pages are omitted.
 */
export const isEverywhereSoft404SitemapUrl = (
  url: string,
  liveUrlSlugs: ReadonlySet<string>,
): boolean => {
  const slug = everywhereTestSlugFromPath(url);
  if (!slug) return false;
  if (slug === 'not-found') return true;
  return shouldCreateEverywhereSoft404Page(slug, liveUrlSlugs);
};

export const everywhereSoft404PrimaryHref = TESTS_INDEX_PATH;
export const everywhereSoft404SecondaryHref = EVERYWHERE_PATH;
